/* Regios Discounts price sync for theme-rendered product lists the app's
   blocks cannot cover. Consumes the JSON payloads emitted by
   snippets/regios-dopp-sync.liquid: asks the app's public DOPP API for the
   discounted price (calculateDiscountedPrices) and paints the theme's own
   price markup — the app's DOM-matching path (updateOnPagePrices) is
   URL-dependent and mispaints on pages like /cart, so we never use it here.
   A MutationObserver picks up payloads injected after load (predictive
   search, cart drawer sections, recently viewed). No-op when the Discounts
   Embed is absent. */
(() => {
  'use strict';

  const SEL = 'script[type="application/json"][data-regios-dopp-sync]:not([data-regios-synced])';
  const ON_SALE_CONTAINER = 'regios-dopp-generic-price--on-sale';
  const PRICE_CELL =
    '.regios-dopp-generic-price-item--sale, .regios-dopp-generic-price-item--regular';
  let api = null;

  // Format a DOPP money object ({ amount in currency units, formatted }) with
  // the theme's own money format, so the painted discount matches the native
  // price rendering (e.g. the "€ 12,34" space the app's own `formatted` drops).
  // marketMoney.format takes cents; DOPP amounts are currency units. Fall back
  // to the app string if the theme formatter is unavailable.
  const fmtMoney = (money) => {
    const mm = window.theme && window.theme.marketMoney;
    if (mm && typeof mm.format === 'function' && money && typeof money.amount === 'number') {
      try {
        return mm.stripHtml(mm.format(Math.round(money.amount * 100)));
      } catch (e) {
        /* fall through to the app-formatted string */
      }
    }
    return money && money.formatted;
  };

  // The app embed also paints PDP / collection cells on load, with Shopify's
  // default money format (no theme spacing), and can land AFTER our paint —
  // clobbering the theme-formatted value. Remember what we wrote per cell and
  // re-assert it if anything overwrites it. Loop-safe: re-setting a value the
  // node already holds is a no-op, so our own writes don't retrigger.
  const desiredText = new WeakMap();
  const watched = new WeakSet();
  let guard = null;

  const observer = () => {
    guard =
      guard ||
      new MutationObserver((muts) => {
        for (const m of muts) {
          let node = m.target;
          while (node && node.nodeType === 1) {
            if (desiredText.has(node)) {
              const want = desiredText.get(node);
              if (node.textContent !== want) node.textContent = want;
              break;
            }
            if (watched.has(node)) {
              reformat(node);
              break;
            }
            node = node.parentNode;
          }
        }
      });
    return guard;
  };

  const guardCell = (el, text) => {
    desiredText.set(el, text);
    if (el.textContent !== text) el.textContent = text;
    observer().observe(el, { childList: true, characterData: true, subtree: true });
  };

  // AAS-4934: the embed repaints these cells with Shopify's raw market format
  // ("8,500.00 kr") instead of the theme's (AAS-604: "8 500,00 kr"), including
  // on undiscounted products, where syncProduct bails before guardCell runs.
  // Re-format whatever it writes, so marketMoney stays the only money
  // authority. Idempotent, so it never loops against the observer above.
  const MONEY_RE = /-?\d[\d.,\s'\u00a0\u202f]*\d|-?\d/;

  const collapse = (value) =>
    String(value)
      .replace(/[\u00a0\u202f]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  // Reads any Shopify separator style. A trailing group is a decimal fraction
  // only when 1-2 digits follow the last separator; three mean it was a
  // thousands group ("1.650" = 1650, not 1.65).
  const toCents = (text) => {
    const digits = text.replace(/[^\d.,-]/g, '');
    const sep = Math.max(digits.lastIndexOf('.'), digits.lastIndexOf(','));
    const frac = sep > -1 ? digits.slice(sep + 1) : '';
    const hasFrac = frac.length > 0 && frac.length < 3;
    const whole = (hasFrac ? digits.slice(0, sep) : digits).replace(/[.,]/g, '');
    if (!/^-?\d+$/.test(whole)) return null;
    return Math.round(Number(whole + '.' + (hasFrac ? frac : '0')) * 100);
  };

  // Currency decoration either side may put around the figure. Longest first,
  // so DKK's theme "kr." wins over the app's "kr" and leaves no stray dot.
  const affixes = () => {
    const config = window.theme.marketMoney.getFormatConfig();
    const sides = [typeof config === 'string' ? config : config.template, window.RegiosDOPP.moneyFormatString]
      .filter(Boolean)
      .map((tpl) => String(tpl).replace(/<[^>]*>/g, '').split(/\{\{[^}]*\}\}/));
    const pick = (i) =>
      sides
        .map((parts) => (parts[i] || '').trim())
        .filter(Boolean)
        .sort((a, b) => b.length - a.length);
    return { prefixes: pick(0), suffixes: pick(1) };
  };

  const strip = (value, list, atEnd) => {
    for (const affix of list) {
      if (atEnd ? value.endsWith(affix) : value.startsWith(affix)) {
        return atEnd ? value.slice(0, -affix.length) : value.slice(affix.length);
      }
    }
    return value;
  };

  const reformat = (el) => {
    if (desiredText.has(el)) return; // a discount we painted — guardCell owns it

    const mm = window.theme && window.theme.marketMoney;
    if (!mm || typeof mm.format !== 'function') return;

    const text = collapse(el.textContent);
    const match = text.match(MONEY_RE);
    if (!match) return;

    const cents = toCents(match[0]);
    if (cents === null) return;

    const money = mm.stripHtml(mm.format(cents));
    if (!money) return; // blank = catalog-market killswitch

    // Drop the old decoration but keep theme wording (the "From " of
    // from_price_html); the theme template re-adds symbol and placement.
    const { prefixes, suffixes } = affixes();
    const before = strip(text.slice(0, match.index).trim(), prefixes, true);
    const after = strip(text.slice(match.index + match[0].length).trim(), suffixes, false);

    const want = collapse(before + ' ' + money + ' ' + after);
    if (text !== want) el.textContent = want;
  };

  const watchCell = (el) => {
    reformat(el);
    if (watched.has(el)) return;
    watched.add(el);
    observer().observe(el, { childList: true, characterData: true, subtree: true });
  };

  const watchAll = (scope) => {
    if (!window.RegiosDOPP) return; // no embed, nothing repaints, Liquid stands
    const root = scope || document;
    if (root.matches && root.matches(PRICE_CELL)) watchCell(root);
    if (root.querySelectorAll) root.querySelectorAll(PRICE_CELL).forEach(watchCell);
  };

  const paintCell = (cell, res) => {
    const container = cell.querySelector('.regios-dopp-generic-price-container');
    if (!container) return;
    const saleEl = container.querySelector('.regios-dopp-generic-price-item--sale');
    const regEl = container.querySelector('.regios-dopp-generic-price-item--regular');
    if (!saleEl || !regEl) return;
    guardCell(saleEl, fmtMoney(res.salePrice));
    guardCell(regEl, fmtMoney(res.regularPrice));
    container.classList.add(ON_SALE_CONTAINER);
    const priceRoot = container.closest('.price');
    if (priceRoot) priceRoot.classList.add('price--on-sale');
  };

  /* The product's own price, formatted by the theme's money authority. Taken
     from the sync payload — Liquid's own number — not from the rendered cell,
     which may already have been repainted by the embed. */
  const nativeMoney = (p) => {
    const cents = p && typeof p.regularPriceInCents === 'number' ? p.regularPriceInCents : null;
    if (cents === null) return null;
    const mm = window.theme && window.theme.marketMoney;
    if (!mm || typeof mm.format !== 'function') return null;
    try {
      return mm.stripHtml(mm.format(cents)) || null;
    } catch (e) {
      return null;
    }
  };

  const syncProduct = async (root, p) => {
    let res;
    const arg = Object.assign({}, p, { quantity: 1 });
    // pass the embed-rendered customer (carries the checked metafields) when
    // present; omit the key entirely when logged out so the app uses its own
    // default context instead of an explicit null override
    if (window.RegiosDOPP.customer) arg.customer = window.RegiosDOPP.customer;
    try {
      res = await api.calculateDiscountedPrices(arg);
    } catch (e) {
      return;
    }
    const own = root.matches('[data-regios-dopp-generic-product-id="' + CSS.escape(String(p.productId)) + '"]')
      ? [root]
      : [];
    const cells = own.concat(
      Array.from(root.querySelectorAll('[data-regios-dopp-generic-product-id="' + CSS.escape(String(p.productId)) + '"]'))
    );

    const discounted =
      res && res.salePrice && res.regularPrice && res.salePrice.amount < res.regularPrice.amount;

    if (discounted) {
      for (const cell of cells) paintCell(cell, res);
      return;
    }

    /* AAS-XXXX: no discount — CLAIM the cell rather than walking away.
       The embed repaints every generic price cell it can see, and on a product
       template it paints them with the PAGE product's price: a card for a 5 590
       Kč accessory in the "Kit yourself" slider rendered the PDP's 14 690 Kč,
       on all four cards. Liquid had it right; the embed overwrote it after
       paint, in Shopify's raw market format ("14.690,00 Kč", dot-grouped)
       rather than the theme's ("14 690,00 Kč").

       Only lists present in the FIRST server response were hit, which is why
       "Continue shopping" on the same page looked fine — it is injected later,
       after the embed's pass, so there was nothing to clobber.

       Pinning the native price through guardCell puts the cell under the same
       ownership a discounted cell already gets: the MutationObserver reverts
       any later foreign write. The value comes from the payload
       (regularPriceInCents, straight from Liquid) rather than from reading the
       DOM, so it cannot be poisoned by a repaint that already happened before
       this runs. */
    const native = nativeMoney(p);
    if (!native) return;
    for (const cell of cells) {
      const container = cell.querySelector('.regios-dopp-generic-price-container');
      const saleEl = container && container.querySelector('.regios-dopp-generic-price-item--sale');
      if (saleEl) guardCell(saleEl, native);
    }
  };

  const syncTag = (tag) => {
    tag.setAttribute('data-regios-synced', 'true');
    let list;
    try {
      list = JSON.parse(tag.textContent);
    } catch (e) {
      return;
    }
    // payloads that must survive load-more extraction sit inside a grid item;
    // they mark the real match root with data-regios-sync-root on an ancestor
    const root = tag.closest('[data-regios-sync-root]') || tag.parentElement;
    if (!root || !Array.isArray(list)) return;
    for (const p of list) {
      if (!p || !p.productId) continue;
      syncProduct(root, p);
    }
  };

  const syncAll = (scope) => {
    (scope || document).querySelectorAll(SEL).forEach(syncTag);
  };

  // The theme re-renders #price-{section} on every variant change
  // (renderProductInfo / updateStickyPrice in theme.js swap innerHTML back to
  // native markup), dropping our painted discount. Re-run the scope's payload
  // with the newly selected variant so the strike-through reappears without a
  // reload. If the new variant isn't discounted, syncProduct no-ops and the
  // fresh native markup stays.
  const resyncScope = (scope, variantId) => {
    const tag = scope.querySelector('script[type="application/json"][data-regios-dopp-sync]');
    if (!tag) return;
    let list;
    try {
      list = JSON.parse(tag.textContent);
    } catch (e) {
      return;
    }
    if (!Array.isArray(list)) return;
    const root = tag.closest('[data-regios-sync-root]') || tag.parentElement;
    if (!root) return;
    for (const p of list) {
      if (!p || !p.productId) continue;
      const entry = variantId ? Object.assign({}, p, { variantId: variantId }) : p;
      syncProduct(root, entry);
    }
  };

  const onVariantChange = (event) => {
    if (!api || !event || !event.data || !event.data.sectionId) return;
    const variant = event.data.variant;
    // product-info, sticky ATC and quick-view containers all carry both a
    // data-section and the product-id hook, and hold the sync payload inside.
    document
      .querySelectorAll(
        '[data-regios-dopp-generic-product-id][data-section="' + CSS.escape(String(event.data.sectionId)) + '"]'
      )
      .forEach((scope) => resyncScope(scope, variant && variant.id));
  };

  const start = () => {
    api = window.RegiosDOPP && window.RegiosDOPP.api && window.RegiosDOPP.api.v0;
    watchAll();
    if (!api) return;
    syncAll();
    new MutationObserver((muts) => {
      for (const m of muts) {
        for (const n of m.addedNodes) {
          if (n.nodeType !== 1) continue;
          if (n.matches && n.matches(SEL)) syncTag(n);
          else if (n.querySelectorAll) syncAll(n);
          watchAll(n);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });

    // Repaint discounts after the theme re-renders the price on variant change.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
    }
  };

  if (window.RegiosDOPP && window.RegiosDOPP.api && window.RegiosDOPP.api.v0) start();
  else window.addEventListener('regios-dopp:api-initialized', start, { once: true });
})();
